import { Injectable } from '@angular/core';
import { ISummaryData } from '../models/summary-data';
import { Observable } from 'rxjs';
import { ConcatenatedHour, Hour, HoursAvailabilityResponse, ProductAvailabilityParams } from '../interfaces/funnel.interface';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';


@Injectable({
  providedIn: 'root'
})
export class ProductAvailabilityService {

    private productsUrl = environment.products_url;
    private identityUrl = environment.identity_url;

  constructor(private http: HttpClient, private translate: TranslateService) { }

  getProductAvailability(
    productId: string,
    selectedDate: Date,
    isSpecificHours: boolean,
    isReturn = false,
    summaryData?: ISummaryData
  ): Observable<any> {
    return new Observable((observer) => {
      this.getProductAvailabilityData({
          productId,
          selectedDate: this.formatDateTime(selectedDate),
          isSpecificHours: isReturn ? isSpecificHours : isSpecificHours,
        })
        .subscribe({
          next: (response: any) => {
            let hours = response
              .filter((r: any) => !r.Disabled)
              .map((r: HoursAvailabilityResponse) => {
                return { Text: r.Text, Key: r.Key, Fee: r.Fee };
              });
            if (summaryData) {
              const destinationServiceDate =
                summaryData.locationInfo?.ServiceEndDate?.split('T')[0];
              const selectedDateString = this.formatDateTime(selectedDate).split('T')[0];

              if (selectedDateString === destinationServiceDate) {
                const [destinationHour, destinationMinute] = (
                  summaryData.locationInfo?.formattedStartTime || ''
                ).split(':');
                const destinationEndTime =
                  parseInt(destinationHour) * 60 + parseInt(destinationMinute);

                hours = hours.filter((hour: any) => {
                  const [hourStr, minuteStr] = hour.Text.split(':');
                  const hourTime = parseInt(hourStr) * 60 + parseInt(minuteStr);
                  return hourTime >= destinationEndTime;
                });
              }
            }

            observer.next(hours);
            observer.complete();
          },
          error: (error) => {
            console.error('Error loading product availability', error);
            observer.error(error);
          }
        });
    });
  }

  getProductAvailabilityData(params: ProductAvailabilityParams) {
    return this.http.get(`${this.productsUrl}/availabilities`, {
      params: { ...params }
    }).pipe(
      catchError((error) => {
        console.error('Error getting product availability:', error);
        throw error;
      })
    );
  }

  transformHours(hours: Hour[]): ConcatenatedHour[] {
    const concatenatedHours: ConcatenatedHour[] = [];
    for (let i = 0; i < hours.length; i += 2) {
      if (i + 1 < hours.length) {
        concatenatedHours.push({
          start: hours[i],
          end: hours[i + 1],
          displayText: `${hours[i].Text} - ${hours[i + 1].Text}`,
        });
      }
    }
    return concatenatedHours;
  }

  formatDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  handleStartTimeChange(
    event: any,
    availableStartHours: HoursAvailabilityResponse[],
    summaryData: ISummaryData
  ): {
    availableEndHours: HoursAvailabilityResponse[];
    noAvailableEndHours: boolean;
    selectedEndHour: string;
    warningMessage: string;
  } {
    const startIndex = event?.Text 
      ? availableStartHours.findIndex((hour) => hour.Text === event.Text) 
      : -1;

    if (startIndex !== -1) {
      const startTime = new Date(`2000-01-01T${event.Text}`);
      const endTime = new Date(startTime.getTime() + 2 * 60 * 60 * 1000);
      const latestEndTime = new Date(`2000-01-01T17:00`);

      const availableEndHours = availableStartHours.filter((hour) => {
        const hourTime = new Date(`2000-01-01T${hour.Text}`);
        return hourTime > endTime && hourTime <= latestEndTime;
      });

      if (availableEndHours.length === 0) {
        if (summaryData.locationInfo?.formattedEndTime) {
          summaryData.locationInfo.formattedEndTime = '';
        }
        return {
          availableEndHours,
          noAvailableEndHours: true,
          selectedEndHour: '',
          warningMessage: this.translate.instant('changePickupTimeOrContactSupport'),
        };
      } else {
        return {
          availableEndHours,
          noAvailableEndHours: false,
          selectedEndHour: availableEndHours[0].Text || '',
          warningMessage: '',
        };
      }
    } else {
      return {
        availableEndHours: [],
        noAvailableEndHours: true,
        selectedEndHour: '',
        warningMessage: '',
      };
    }
  }

  resetTimeSlotSelection(): {
    selectedStartHour: null;
    selectedEndHour: string;
    availableStartHours: HoursAvailabilityResponse[];
    availableEndHours: HoursAvailabilityResponse[];
    selectedHourPair: null;
    noAvailableHours: boolean;
    showDropdown: boolean;
  } {
    return {
      selectedStartHour: null,
      selectedEndHour: '',
      availableStartHours: [],
      availableEndHours: [],
      selectedHourPair: null,
      noAvailableHours: false,
      showDropdown: false,
    };
  }

}
